import TiptapEditorContextProvider from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import { MediaGalleryDialog } from "@src/lib/ui/MediaGalleryDialog";
import { type MediaGalleryItem } from "@src/lib/ui/MediaGallery";
import { cn } from "@src/lib/utils/cn";
import { FC, MouseEvent, useState } from "react";
import RichTextEditor from "../course/RichTextEditor";

type RichTextProps = {
  text: string;
  /** Opus Docs treat the first block as a large title; disable for plain read-only snippets. */
  disableDocumentTitleStyle?: boolean;
  disableImageGallery?: boolean;
  /** Passed to the image gallery, e.g. a `z-*` when rendered inside a raised dialog. */
  imageGalleryClassName?: string;
};

const isEnlargeableImage = (img: HTMLImageElement) =>
  /^https?:/i.test(img.getAttribute("src") ?? "") &&
  !(img.complete && img.naturalWidth === 0) &&
  !img.closest('a, [data-type="content-link"]');

const ReadOnlyTrainingRichTextDocument: FC<RichTextProps> = ({
  text,
  disableDocumentTitleStyle = false,
  disableImageGallery = false,
  imageGalleryClassName,
}) => {
  const [gallery, setGallery] = useState<{
    items: MediaGalleryItem[];
    index: number;
  } | null>(null);

  const onClick = (e: MouseEvent<HTMLDivElement>) => {
    const target = e.target;
    if (!(target instanceof Node) || !e.currentTarget.contains(target)) {
      e.stopPropagation();
      return;
    }
    if (
      disableImageGallery ||
      !(target instanceof HTMLImageElement) ||
      !isEnlargeableImage(target)
    ) {
      return;
    }
    e.stopPropagation();
    const images = Array.from(e.currentTarget.querySelectorAll("img")).filter(
      isEnlargeableImage,
    );
    setGallery({
      items: images.map((img) => ({ url: img.src, type: "image" })),
      index: images.indexOf(target),
    });
  };

  return (
    <div className="self-stretch" onClick={onClick}>
      <TiptapEditorContextProvider
        tiptapEditorId={`rich-text-read-only-${text}`}
      >
        <RichTextEditor
          key={`rich-text-read-only-${text}`}
          // eslint-disable-next-line tailwindcss/no-custom-classname
          className={cn(
            "rich-text-opus-doc-editor",
            disableDocumentTitleStyle &&
              "rich-text-opus-doc-editor--no-doc-title",
          )}
          text={text || ""}
          disableTextVariants
          onChange={() => {}}
          hasEditContentPermission={false}
          enableMathematics={true}
        />
      </TiptapEditorContextProvider>
      {gallery && (
        <MediaGalleryDialog
          items={gallery.items}
          initialIndex={gallery.index}
          open
          onOpenChange={(open) => !open && setGallery(null)}
          className={imageGalleryClassName}
        />
      )}
    </div>
  );
};

export default ReadOnlyTrainingRichTextDocument;
